import React from 'react'
import {
  Card,
  CardContent,
  Badge,
  Button,
  Progress,
  Avatar,
} from '@/shared/ui'
import type { SuggestionWithStats } from '../hooks/useSuggestions'
import { prepareCommunityMessageContent } from '@/modules/comunidade/utils/communityMessageContent'

type Props = {
  item: SuggestionWithStats
  isMine: boolean
  isAuthor: boolean
  canModerate: boolean
  onVote: (id: string, action: 'up' | 'down' | 'clear') => void
  onRequestEdit?: (item: SuggestionWithStats) => void
  onRequestReview?: (id: string, action: 'approve' | 'reject') => void
  onRequestDelete?: (id: string) => void
}

export default function SuggestionCard({
  item,
  isMine,
  isAuthor,
  canModerate,
  onVote,
  onRequestEdit,
  onRequestReview,
  onRequestDelete,
}: Props) {
  const hasTarget = item.targetMin > 0
  const reachedTarget = hasTarget && item.votesUp >= item.targetMin

  const communityApproved =
    item.status === 'approved' || reachedTarget
  const communityRejected = item.status === 'rejected'
  const isVotingClosed = communityApproved || communityRejected

  const isOpenForVoting =
    !isVotingClosed &&
    (item.status === 'published' || item.status === 'approved')

  const showReviewNote =
    !!item.review_note &&
    (item.review_note_visibility === 'public' ||
      (isAuthor && item.review_note_visibility === 'author'))

  const showAuthorPrivateNote =
    !!item.author_private_note && (isAuthor || canModerate)

  const statusLabel = getStatusLabel(item.status)
  const remainingVotes =
    item.targetMin > 0 ? Math.max(item.targetMin - item.votesUp, 0) : 0
  const canShowAuthor = !!item.author_name || !!item.author_avatar_url

  const userVote = item.currentUserVote
  const upSelected = userVote === 'up'
  const downSelected = userVote === 'down'

  const headerBadgeColor = communityApproved
    ? 'success'
    : communityRejected
    ? 'error'
    : badgeColor(item.status)

  const headerBadgeLabel =
    communityApproved || communityRejected
      ? communityApproved
        ? 'Aprovada pela comunidade'
        : 'Não aprovada pela comunidade'
      : statusLabel

  return (
    <Card>
      <CardContent className="space-y-4 p-4">
        {/* Título no topo */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="break-words text-base font-semibold">
              {item.title}
            </div>
          </div>

          {canModerate && item.status === 'pending' && (
            <div className="flex flex-none gap-2">
              <Button
                variant="outline"
                type="button"
                onClick={() => onRequestReview?.(item.id, 'approve')}
              >
                Aprovar
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => onRequestReview?.(item.id, 'reject')}
              >
                Negar
              </Button>
            </div>
          )}
        </div>

        {/* Descrição */}
        <div
          className="prose prose-invert mt-1 max-w-none text-sm text-foreground/80"
          dangerouslySetInnerHTML={{ __html: prepareCommunityMessageContent(item.content || '').html }}
        />

        {/* Motivo da equipe (público ou apenas autor) */}
        {showReviewNote && (
          <div className="rounded-md bg-[#0f0f0f] p-3 text-xs text-foreground/80">
            <div className="mb-2 flex items-center justify-end gap-2">
              {item.review_note_visibility === 'author' && (
                <span className="text-[10px] uppercase tracking-wide text-foreground/60">
                  Visível apenas para você
                </span>
              )}
              <Badge variant="soft" color="info">
                Motivo da equipe
              </Badge>
            </div>
            <div className="mt-1 whitespace-pre-line">
              {item.review_note}
            </div>
          </div>
        )}

        {/* Bloco de votação (quando em votação) */}
        {isOpenForVoting && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-foreground/70">
              <span>
                Aprovação da comunidade:{' '}
                <span className="font-medium text-foreground">
                  {item.votesUp} voto(s) a favor
                </span>
                {item.votesDown > 0 && (
                  <span className="ml-2">/ {item.votesDown} contra</span>
                )}
              </span>
              {remainingVotes > 0 && (
                <span>
                  Faltam{' '}
                  <span className="font-medium text-foreground">
                    {remainingVotes} voto
                    {remainingVotes === 1 ? '' : 's'}
                  </span>{' '}
                  para ser aprovada.
                </span>
              )}
            </div>

            <Progress value={item.progress} />

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={upSelected ? 'destructive' : 'outline'}
                  onClick={() =>
                    onVote(item.id, upSelected ? 'clear' : 'up')
                  }
                >
                  {upSelected
                    ? 'Remover voto'
                    : 'Quero que seja implementada'}
                </Button>

                <Button
                  type="button"
                  variant={downSelected ? 'destructive' : 'outline'}
                  onClick={() =>
                    onVote(item.id, downSelected ? 'clear' : 'down')
                  }
                >
                  {downSelected
                    ? 'Remover voto'
                    : 'Não faz sentido para mim'}
                </Button>

                {canModerate && (
                  <>
                    {onRequestEdit && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => onRequestEdit(item)}
                      >
                        Editar
                      </Button>
                    )}
                    {onRequestDelete && (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => onRequestDelete(item.id)}
                      >
                        Excluir
                      </Button>
                    )}
                  </>
                )}
              </div>

              {userVote && (
                <div className="flex items-center gap-2 text-xs text-foreground/70">
                  <span>Seu voto:</span>
                  <Badge
                    variant="soft"
                    color={userVote === 'up' ? 'success' : 'error'}
                  >
                    {userVote === 'up' ? 'A favor' : 'Contra'}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Resultado quando votação encerrada */}
        {isVotingClosed && (
          <div className="space-y-1 text-xs text-foreground/70">
            <div>
              Resultado da votação da comunidade:{' '}
              <span className="font-medium text-foreground">
                {item.votesUp} voto(s) a favor
              </span>
              {item.votesDown > 0 && (
                <span className="ml-2">/ {item.votesDown} contra</span>
              )}
            </div>
          </div>
        )}

        {/* Mensagem exclusiva para o autor */}
        {showAuthorPrivateNote && (
          <div className="rounded-md bg-[#0f0f0f] p-3 text-xs text-foreground/80">
            <div className="mb-2 flex items-center justify-end gap-2">
              <Badge variant="soft" color="success">
                Mensagem exclusiva para você
              </Badge>
            </div>
            <div className="mt-1 whitespace-pre-line">
              {item.author_private_note}
            </div>
          </div>
        )}

        {/* Rodapé com status + autor */}
        <div className="mt-2 flex flex-wrap items-center justify-between gap-3 text-[11px] text-foreground/70">
          <div className="flex items-center gap-2">
            <Badge
              variant="soft"
              color={headerBadgeColor}
              className="px-2 py-0.5 text-[11px]"
            >
              {headerBadgeLabel}
            </Badge>
            {isMine && (
              <Badge
                variant="soft"
                color="info"
                className="px-2 py-0.5 text-[11px]"
              >
                Minha sugestão
              </Badge>
            )}
          </div>

          {canShowAuthor && (
            <div className="flex items-center gap-2">
              <Avatar
                src={item.author_avatar_url || undefined}
                alt={item.author_name || 'Autor'}
              />
              <div className="flex flex-col text-right leading-tight">
                <span className="max-w-[150px] truncate text-xs font-medium sm:max-w-[220px]">
                  {item.author_name || 'Usuário'}
                </span>
                <span className="text-[10px] text-foreground/60">
                  Autor da sugestão
                </span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

type SuggestionStatus = SuggestionWithStats['status']

function getStatusLabel(status: SuggestionStatus): string {
  switch (status) {
    case 'pending':
      return 'Em análise'
    case 'published':
      return 'Em votação'
    case 'approved':
      return 'Aprovada'
    case 'rejected':
      return 'Negada'
    default:
      return status
  }
}

function badgeColor(
  status: SuggestionStatus,
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'pending':
      return 'warning'
    case 'published':
      return 'info'
    case 'approved':
      return 'success'
    case 'rejected':
      return 'error'
    default:
      return 'default'
  }
}

